import formurlencoded from 'form-urlencoded'
import queryString from 'query-string'

import { pagination, campaignTypes, isOdosWeek } from './lib/constants';

const setWindowLocationIfApiUrlEmpty = (url) => {
  if (url === '' )
    return `${window.location.origin}`;
  else
    return url;
}
const API_URL = `${setWindowLocationIfApiUrlEmpty(process.env.REACT_APP_API_URL || '')}/api/v1`
const NODE_API_URL = `${setWindowLocationIfApiUrlEmpty(process.env.REACT_APP_API_URL || '')}/node/api/v1`

const API_URL_V0 = `${setWindowLocationIfApiUrlEmpty(process.env.REACT_APP_API_URL || '')}/v0`
const NODE_API_URL_V0 = `${setWindowLocationIfApiUrlEmpty(process.env.REACT_APP_API_URL || '')}/node/api/v0`


const setStrapiLocation = () => {
  if(typeof window !== 'undefined' && window.location.host.includes("storyweaver.org.in")) {
    return 'https://strapi.storyweaver.org.in/api';
  }
  else
    return 'https://strapi.pbees.party/api';
}
const STRAPI_API = setStrapiLocation()

const getWithCredentials = url => fetch(url, {
  credentials: 'include',
  headers: {
    'locale': localStorage.getItem('locale') || 'en'
  }
});

const postWithCredentials = (url, body, contentType = 'application/json') => fetch(url, {
  method: 'POST',
  body,
  credentials: 'include',
  headers: {
    'Content-Type': contentType,
    'locale': localStorage.getItem('locale') || 'en'
  }
});

// for 'multipart/form-data' 'content-type' will be undefined so
// that the browser auto detects data field boundaries.

const postMultiPartWithCredentials = (url, body) => fetch(url, {
  // mode: 'no-cors', // enable for posting to localhost:xxxx
  method: 'POST',
  body,
  credentials: 'include',
  headers: {
    'locale': localStorage.getItem('locale') || 'en'
  }
});


// Converts filters from a form supplied by our filter bar/filter panel
// into a form expected by the backend.
// Why? Because like idiots we hardcoded a bunch of these keys in the
// filters component, which is really complex. Changing one thing
// breaks a million other things, so it's easiest to do the translation
// here. If you're reading this in the future, pliss fix the filters
// bar and filters panel.
const sanitizeFilters = (appliedFilters) => {
  const query = { };

  if (!appliedFilters) {
    return query;
  }

  const optionalFilters = {
    category: 'categories',
    publisher: 'publishers',
    level: 'levels',
    language: 'languages',
    story_type: 'story_type',
    query: 'query',
    tags: 'tags',
    sort: 'sort',
    style: 'styles',
    // This query string is passed to the URL, when a user clicks on
    // 'More Stories' link from Image details page.
    illustration_slug: 'illustration_slug',
    bulk_download: 'bulk_download'
  };

  Object.keys(optionalFilters).forEach(filterKey => {
    if (appliedFilters[filterKey] && appliedFilters[filterKey].length > 0) {
      query[optionalFilters[filterKey]] = appliedFilters[filterKey];
    }
  });

  return query;
}


export class User {
  static fetchProfile(slug, perPage = 24, page = 1) {
    return getWithCredentials(
      `${NODE_API_URL}/users/${slug}?${queryString.stringify({perPage: perPage, page: page})}`
    );
  }

  static fetchMore(profileName, slug, collection, page, perPage) {
    if (collection === 'illustrations') {
      return getWithCredentials(
        `${NODE_API_URL}/illustrations-search?search[illustrator_slugs]=${slug}&page=${page}&per_page=${perPage}`
      );
    }
  }

  static fetchMe(isRP) {
    return getWithCredentials(`${NODE_API_URL}/me${isRP ? '?feature=rp' : ''}`);
  }

  static fetchNotifications() {
    return getWithCredentials(`${API_URL}/flash_notice`);
  }

  static fetchPlatformNotifications() {
    return getWithCredentials(`${NODE_API_URL}/notifications`);
  }

  static markAllNotificationsAsRead() {
    return postWithCredentials(`${NODE_API_URL}/notification/read_all`, '');
  }

  static markOneNotificationsAsRead(id) {
    return postWithCredentials(`${NODE_API_URL}/notification/read_one?id=${id}`, '');
  }

  static login(email, password, rememberMe) {
    return fetch(`${API_URL}/users/sign_in`, {
      method: 'POST',
      credentials: 'include',
      body: formurlencoded({
        'api_v1_user[email]': email,
        'api_v1_user[password]': password,
        'api_v1_user[remember_me]': rememberMe
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static logout() {
    return fetch(`${API_URL}/users/sign_out`, {
      method: 'DELETE',
      credentials: 'include'
    })
  }

  static signup(details) {
    return fetch(`${API_URL}/users`, {
      method: 'POST',
      credentials: 'include',
      body: queryString.stringify(details),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static forgotPassword(email) {
    return fetch(`${API_URL}/users/password`, {
      method: 'POST',
      credentials: 'include',
      body: `api_v1_user[email]=${email}`,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static resetPassword(password, confirmPassword, resetPasswordToken) {
    return fetch(`${API_URL}/users/password`, {
      method: 'PUT',
      credentials: 'include',
      body: queryString.stringify({
        'api_v1_user[password]': password,
        'api_v1_user[password_confirmation]': confirmPassword,
        'api_v1_user[reset_password_token]': resetPasswordToken
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static resendConfirmationLink(email) {
    return fetch(`${API_URL}/users/confirmation`, {
      method: 'POST',
      credentials: 'include',
      body: `api_v1_user[email]=${email}`,
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static emailConfirmation(confirmationToken) {
    const qs = queryString.stringify({ confirmation_token: confirmationToken });
    return getWithCredentials(`${API_URL}/users/confirmation/?${qs}`);
  }

  static getTpStories() {
    return getWithCredentials(`${NODE_API_URL}/profile/get_tp_stories`);
  }

  static thirdPartyUserLogin(confirmationToken) {
    return fetch(`${API_URL}/third-party-user-login`, {
      method: 'POST',
      credentials: 'include',
      body: queryString.stringify({
        'user_token': confirmationToken,
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static facebookAuth(name, email, uid) {
    return fetch(`${API_URL}/auth/facebook/callback`, {
      method: 'PATCH',
      credentials: 'include',
      body: queryString.stringify({
        'auth[name]': name,
        'auth[email]': email,
        'auth[uid]': uid
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static googleAuth(name, email, uid) {
    return fetch(`${API_URL}/auth/google_oauth2/callback?auth[name]=${name}&auth[email]=${email}&auth[uid]=${uid}`, {
      method: 'PATCH',
      credentials: 'include',
      body: queryString.stringify({
        'auth[name]': name,
        'auth[email]': email,
        'auth[uid]': uid
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static fetchLists() {
    return getWithCredentials(`${NODE_API_URL}/me/lists`);
  }

  static deleteList(listId) {
    return fetch(`${NODE_API_URL}/lists/${listId}`, {
      method: 'DELETE',
      credentials: 'include'
    })
  }

  static changeLanguage(newLanguage, browserLang) {
    if(browserLang) return fetch(`${NODE_API_URL}/set-locale/${newLanguage}?browser_preferences=${browserLang}`, {
      method: 'POST',
      credentials: 'include',
    })
    else return fetch(`${NODE_API_URL}/set-locale/${newLanguage}`, {
      method: 'POST',
      credentials: 'include',
    })
  }

  static fetchEmailStatus(email) {
    return fetch(`${NODE_API_URL}/user/status?email=${email}`)
  }

  static changeOfflinePopUpStatus() {
    return fetch(`${NODE_API_URL}/user/offline-book-popup-seen`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
  }

  static changeUserFeedbackModalStatus(feedbackType) {
    return fetch(`${NODE_API_URL}/user/popup-seen?popupType=${feedbackType}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    });
  }

  static updateConsent() {
    return fetch(`${NODE_API_URL}/update_consent_flag`, {
      method: 'POST',
      credentials: 'include'
    })
  }

  static submitUserProfile(user_type , description , user_id) {
    return fetch(`${NODE_API_URL}/profiles/${user_id}`, {
      method: 'PATCH',
      credentials: 'include',
      body: queryString.stringify({
        'type' : user_type,
        'description': description,
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static saveUserProfileData(user_id, userDetails) {
    return fetch(`${API_URL}/users/${user_id}/edit`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(userDetails),
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  static changePassword(user_id, changePassword) {
    return fetch(`${API_URL}/users/${user_id}/update_password`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(changePassword),
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  static saveOrganisation(organisationDetails) {
    return fetch(`${NODE_API_URL}/organizations/organization_sign_up`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify(organisationDetails),
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  static autoCompleteUserData(user_type , searchValue) {
    return fetch(`${NODE_API_URL}/profiles/autocomplete_for_user_profiling?type=${user_type}&query=${searchValue}`)
  }

  static fetchOrganisationNames(org_name) {
    return fetch(`${NODE_API_URL_V0}/organizations/autocomplete?query=${org_name}`)
  }

  static fetchOrganisationDetails(org_name, countryName) {
    return fetch(`${NODE_API_URL_V0}/organizations/validate_org_name_country?org_name=${org_name}&country=${countryName}`)
  }

  static OTPSignup(userDetails) {
    return fetch(`${API_URL}/create_user_phone_number`, {
      method: 'POST',
      credentials: 'include',
      body: queryString.stringify(userDetails),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static resendOTP(phoneNumber, type) {
    // TODO: update regex for other countries
    // convert +91 12345-67890 format to %2B911234567890
    const encodedPhoneNumber = encodeURIComponent(phoneNumber.replace(/\s+|-/g,''));
    return getWithCredentials(`${API_URL}/user/resend_otp?phone_number=${encodedPhoneNumber}&type=${type}`);
  }

  static OTPLogin(otp, phoneNumber) {
    const encodedPhoneNumber = encodeURIComponent(phoneNumber.replace(/\s+|-/g,''));
    return getWithCredentials(`${API_URL}/create_otp_session?otp=${otp}&phone_number=${encodedPhoneNumber}`);
  }

  static generateOTP(phoneNumber) {
    const encodedPhoneNumber = encodeURIComponent(phoneNumber.replace(/\s+|-/g,''));
    return getWithCredentials(`${API_URL}/user/login_user_otp?phone_number=${encodedPhoneNumber}`);
  }

  static fetchPhoneNumberStatus(phoneNumber) {
    const encodedPhoneNumber = encodeURIComponent(phoneNumber.replace(/\s+|-/g,''));
    return getWithCredentials(`${NODE_API_URL}/user/status_phone_number?phone_number=${encodedPhoneNumber}`);
  }

  static updateDonatePopupStatus() {
    return fetch(`${NODE_API_URL}/user/popup-seen?popupType=DonationPopup`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    })
  }

  static submitDonorDetails(userDetails) {
    return fetch(`${NODE_API_URL}//international_donor_gating_form`, {
      method: 'POST',
      body: JSON.stringify(userDetails),
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    })
  }

  static fetchAutoSuggestions(value, showLangSuggestion, limit) {
    return fetch(`${NODE_API_URL}/global-auto-suggestions/suggest?value=${value}&limit=${limit}${showLangSuggestion ? '&language_suggestion=true' : ''}`);
  }

  static updateWordSearchFrequency(id) {
    return fetch(`${NODE_API_URL}/global-auto-suggestions/word-search/${id}`)
  }

  static addNewSearchWord(value) {
    return fetch (`${NODE_API_URL}/global-auto-suggestions/word-search/new?value=${value}`)
  }

  static validateSearchWord(value) {
    return fetch (`${NODE_API_URL}/global-auto-suggestions/word-search/validate?value=${value}`)
  }

  static registerCbseBookshelf( user_id, list_id, grade, school,affiliation, city, state, user_name, lang, code) {
    return postWithCredentials(`${NODE_API_URL_V0}/contests/cbse/registration?user_id=${user_id}&list_id=${list_id}&grade=${grade}&school=${school}&affiliation_number=${affiliation}&city=${city}&state=${state}&participant_name=${user_name}&language=${lang}&nomination_code=${code}`);
  }

  static getStaticDataCbse(user_id, campaign_type, list_id) {
    return getWithCredentials(`${NODE_API_URL_V0}/contests/cbse/static_data?user_id=${user_id}&campaign_type=${campaign_type}&list_id=${list_id} `);   
  }
  
  static registerCampaignBookshelf( user_id, list_id, grade, school, block, city, district, state, user_name, lang, campaignType, autoEnroll=false) {
    const encodedGrade = encodeURIComponent(grade);
    return postWithCredentials(`${NODE_API_URL}/campaign/registration?user_id=${user_id}&list_id=${list_id}&grade=${encodedGrade}&school=${school}&block=${block}&city=${city}&state=${state}&participant_name=${user_name}&language=${lang}&district=${district}&campaign_type=${campaignType}&auto_enroll=${autoEnroll}`);
  }

  static getCampaignCertificate(reg_id, certificateData) {
    return postWithCredentials(`${NODE_API_URL}/campaign/certificate`, JSON.stringify({
      'registration_id': reg_id,
      ...certificateData
    }))
  }

  static getUserCampaignData(user_id, campaign_type, list_id) {
    return fetch(`${NODE_API_URL}/campaign/static_data?user_id=${user_id}&campaign_type=${campaign_type}&list_id=${list_id}`); 
  }
}

export class Organisation {
  static fetchProfile(slug, perPage) {
    return getWithCredentials(`${NODE_API_URL}/organisations/${slug}?perPage=${perPage}`);
  }

  static search(searchValue) {
    return fetch(`${NODE_API_URL}/organizations/autocomplete?query=${searchValue}`)
  }

  static fetchAll(appliedFilters, page = 1, perPage = pagination.organisationsSearch) {
    const sanitizedFilters = sanitizeFilters(appliedFilters);

    const query = Object.assign({
      page,
      per_page: perPage
    }, sanitizedFilters);

    const qs = queryString.stringify(query, { arrayFormat: 'bracket' });
    return getWithCredentials(`${NODE_API_URL}/org-search?${qs}`);
  }
}

export class Publisher {
  static fetchProfile(slug, perPage = 24, page = 1) {
    return getWithCredentials(
      `${NODE_API_URL}/publishers/${slug}?${queryString.stringify({perPage: perPage, page: page})}`
    );
  }

  static fetchMore(profileName, slug, collection, page, perPage) {
    if (collection === 'editorsPick') {
      return getWithCredentials(
        `${NODE_API_URL}/books-search?${queryString.stringify({'publishers[]': profileName, page: page, per_page: perPage, sort: "Editor's Picks" })}`
      );
    } else {
      return getWithCredentials(
        `${NODE_API_URL}/books-search?${queryString.stringify({'publishers[]': profileName, page: page, per_page: perPage, sort: "New Arrivals" })}`
      );
    }
  }
}

export class TranslationPartner {
  static fetchProfile(slug, perPage = 24, page = 1) {
    return getWithCredentials(
      `${NODE_API_URL}/translation-partners/${slug}?${queryString.stringify({perPage: perPage, page: page})}`
    );
  }
}


export class Newsletter {
  static subscribe(email) {
    return fetch(`${NODE_API_URL}/subscription`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      body: formurlencoded({
        email
      })
    });
  }
}


export class ReadingList {
  static fetchDetails(slug) {
    return getWithCredentials(`${NODE_API_URL}/lists/${slug}`);
  }

  static update(readingList) {
    return fetch(`${NODE_API_URL}/lists/${readingList.id}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify(readingList)
    });
  }

  static postLike(slug) {
    return postWithCredentials(`${NODE_API_URL}/lists/${slug}/like`, '');
  }

  static create(title, description, books = []) {
    return postWithCredentials(`${NODE_API_URL}/lists`, JSON.stringify({
      title,
      description,
      books
    }));
  }

  static fetchAllFilters() {
    return getWithCredentials(`${NODE_API_URL}/lists/filters`);
  }

  static search(appliedFilters, page = 1, perPage = pagination.allLists) {
    const sanitizedFilters = sanitizeFilters(appliedFilters);

    const query = Object.assign({
      page,
      per_page: perPage
    }, sanitizedFilters);

    const qs = queryString.stringify(query, { arrayFormat: 'bracket' });
    return getWithCredentials(`${NODE_API_URL}/lists-search?${qs}`);
  }

  static fetchListHome() {
    return getWithCredentials(`${NODE_API_URL}/lists/home`);
  }

  static fetchRecommendedLists() {
    return getWithCredentials(`${NODE_API_URL}/lists/recommended`);
  }

  static checkStoryStatus(registrationId, campaignType){
    if(campaignType === campaignTypes.cbse_contest){
      return fetch(`${NODE_API_URL_V0}/contests/cbse/stories_status?registration_id=${registrationId}&campaign_type=${campaignType}`)
    }else{
      return fetch(`${NODE_API_URL}/campaign/stories_status?registration_id=${registrationId}&campaign_type=${campaignType}`);
    }
  }

  static setStoryStatus(storyId, registrationId, storyStatus, campaignType){
    console.log('campaignType' , campaignType, 'storyStatus' , storyStatus)
    if(campaignType === campaignTypes.cbse_contest){
      return postWithCredentials(`${NODE_API_URL_V0}/contests/cbse/reading_status?story_id=${storyId}&registration_id=${registrationId}&reading_status=${storyStatus}&campaign_type=${campaignType}`)
    }else{
      return postWithCredentials(`${NODE_API_URL}/campaign/story_reading_status?story_id=${storyId}&registration_id=${registrationId}&reading_status=${storyStatus}&campaign_type=${campaignType}`);
    }
  }
}


export class Translation {
  static fetchBooks(sourceLanguage, targetLanguage, filters, page, mobileTranslate=false, perPage) {
    const options = {
      source_language: sourceLanguage,
      target_language: targetLanguage,
      page: page,
      per_page: perPage
    }

    const optionalFilters = {
      category: 'categories',
      publisher: 'publishers',
      level : 'reading_levels'
    };

    Object.keys(optionalFilters).forEach(f => {
      if (filters[f] && filters[f].length > 0) {
        options[optionalFilters[f]] = filters[f];
      }
    });

    if(mobileTranslate){
      options['mobile_translate']= true
    }

    return getWithCredentials(
      `${NODE_API_URL}/books-for-translation?${queryString.stringify(options, {arrayFormat: 'bracket'})}`
    )
  }
  static fetchFilters() {
    return getWithCredentials(
      `${NODE_API_URL}/books/translate-filters`
    )
  }

  static fetchCheckTranslations(slug, translateToLanguage) {
    return getWithCredentials(
      `${API_URL}/stories/${slug}/check-translations?${queryString.stringify({translateToLanguage: translateToLanguage})}`
    )
  }

  static mobileTranslate(slug, translateToLanguage){
    return getWithCredentials(
      `${API_URL_V0}/editor/${slug}/translate?to_language=${translateToLanguage}&mobile_translate=${true}`
    )
  }
}


export class Book {
  static fetch(slug , isCsf) {
    return getWithCredentials(`${NODE_API_URL}/stories/${slug}${isCsf ? '?caller=resource' : ''}`);
  }

  static fetchExtendedBook(id) {
    return getWithCredentials(`${NODE_API_URL}/stories/${id}/translations_and_videos`);
  }

  static addToEditorsPicks(slug) {
    return postWithCredentials(`${NODE_API_URL}/stories/${slug}/add_to_editor_picks`);
  }

  static removeFromEditorsPicks(slug) {
    return postWithCredentials(`${NODE_API_URL}/stories/${slug}/remove_from_editor_picks`);
  }

  static removeFromList(slug, listSlug) {
    return fetch(
      `${NODE_API_URL}/lists/${listSlug}/remove_story/${slug}`,
      {
        method: 'DELETE',
        credentials: 'include'
      }
    );
  }

  static submitSuggestion(postSuggestPayload) {
    return postWithCredentials(`${NODE_API_URL}/add_suggestion`, JSON.stringify(postSuggestPayload));
  }

  static fetchSuggestions(query) {
    return getWithCredentials(`${NODE_API_URL}/get_suggestion?${queryString.stringify(query)}`);
  }

  static flag(slug, reasons) {
    return postWithCredentials(`${NODE_API_URL}/stories/${slug}/flag`, JSON.stringify(reasons));
  }

  static editBook(slug, name , reason) {
    return postWithCredentials(`${NODE_API_URL}/stories/${slug}/story_edit_reasons/?${queryString.stringify({reasons: reason , name : name })}`);
  }

  static submitSmileyRating(storySlug, reaction) {
    return postWithCredentials(`${NODE_API_URL}/smiley_ratings`, JSON.stringify({storySlug, reaction}));
  }

  static submitTranslationEditorFeedback(selectedReasons, rating) {
    return postWithCredentials(`${NODE_API_URL}/translation_editor_feedback`, JSON.stringify({translation_editor_feedback: {rating, reasons: selectedReasons }}));
  }

  static submitGatingForm(postGatingFormPayload) {
    return postWithCredentials(`${NODE_API_URL}/stories/reset_story_download_count`, JSON.stringify(postGatingFormPayload));
  }

  static submitImageGatingForm(postImageGatingFormPayload) {
    return postWithCredentials(`${NODE_API_URL}/illustrations/reset_illustration_download_count`, JSON.stringify(postImageGatingFormPayload));
  }

  static submitImgGatingForm(postImgGatingFormPayload) {
    return postWithCredentials(`${NODE_API_URL}/users/download_request`, JSON.stringify({ download_request: postImgGatingFormPayload }));
  }

  static submitOfflineTranslateData(bookId, language, postOfflineTranslatePayload) {
    return postWithCredentials(`${API_URL}/offline_translate?story_id=${bookId}&language_name=${language}`,JSON.stringify(postOfflineTranslatePayload));
  }

  static fetchNextTranslationBooks(slug , translateToLanguage) {
    return getWithCredentials(`${NODE_API_URL}/stories/${slug}/translate_recommendations?${queryString.stringify({translateToLanguage})}`);
  }

  static relevel(slug, newLevel) {
    return postWithCredentials(`${NODE_API_URL}/stories/${slug}/relevel`, JSON.stringify({slug, newLevel}));
  }

  static addAward(slug, awardDescription) {
    return postWithCredentials(`${NODE_API_URL}/stories/${slug}/add_award`, JSON.stringify({slug, awardDescription}));
  }

  static updateLanguage(slug, newLanguage) {
    return postWithCredentials(`${API_URL}/stories/${slug}/update_language`, JSON.stringify({slug, newLanguage}));
  }

  static resolveSuggestions(suggestion_id , page_content) {
      return fetch(`${NODE_API_URL}/suggestions/resolve/${suggestion_id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({suggestion_id , page_content })
      });
    }

  static blockStory(story_id) {
    return fetch(`${NODE_API_URL}/suggestions/block_story_for_suggestions/${story_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({story_id})
    });
  }

  static blockUser(user_id) {
    return fetch(`${NODE_API_URL}/suggestions/block_user_for_suggestions/${user_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({user_id})
    });
  }

  static updateAward(slug, awardDescription, awardId) {
    return fetch(`${NODE_API_URL}/stories/${slug}/update_award/${awardId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({slug , awardDescription , awardId})
    });
  }

  static fetchAssets(slug, isAudio=false, ignoreCount=false , isCsf, isFlpHome, isStem, org_id, embed) {
    return getWithCredentials(`${API_URL}/stories/${slug}/read?${isAudio ? queryString.stringify({isAudio: isAudio}) : ''}&ignore_count=${ignoreCount}&${queryString.stringify({ source : isCsf ? 'csf': isFlpHome ? 'flp_at_home': isStem ? 'stem' : ''})}&${org_id ? `org_id=${org_id}`: ""}&${embed ? `embed=${embed}`: ""}`);
  }

  static fetchAllFilters(from_storyfinder=false, lang) {
    return fetch(`${NODE_API_URL}/books/filters${from_storyfinder ? '?storyfinder=true' : ''}`, {
      credentials: 'include',
      headers: {
        'locale': lang || localStorage.getItem('locale') || 'en'
      }
    });
  }

  static fetchReadalongFilters() {
    return getWithCredentials(`${NODE_API_URL}/books/readalong-lang-filters`);
  }

  static fetchAll(appliedFilters, page = 1, perPage = pagination.allBooks, from_storyfinder=false) {
    const sanitizedFilters = sanitizeFilters(appliedFilters);

    const query = Object.assign({
      page,
      per_page: perPage
    }, sanitizedFilters);

    const qs = queryString.stringify(query, { arrayFormat: 'bracket' });
    return getWithCredentials(`${NODE_API_URL}/books-search?${qs}${from_storyfinder ? '&from_storyfinder=true' : ''}`);
  }

  static like(slug) {
    return postWithCredentials(`${NODE_API_URL}/stories/${slug}/like`);
  }

  static fetchCategoryHeaderImage(slug) {
    return getWithCredentials(`${NODE_API_URL}/category-banner/?${queryString.stringify({name: slug})}`);
  }

  static fetchTagHeaderImage(tag) {
    return getWithCredentials(`${NODE_API_URL}/tag-banner?tag_name=${tag}`);
  }

  static fetchConfirmStoryFormat(query) {
    return getWithCredentials(`${NODE_API_URL}/confirm_story_formats?${queryString.stringify(query)}`)
  }

  static fetchStoryPages(id) {
    return getWithCredentials(`${NODE_API_URL}/story_pages/${id}`)
  }
  static fetchAssetsForOfflineTranslate(slug) {
    return getWithCredentials(`${API_URL}/stories/${slug}/read?show_content_number=true&get_offline_story_details=true&story_pages=true`);
  }

  static submitOfflineTranslationFeedback(offlineTranslationFeedback) {
    return postWithCredentials(`${NODE_API_URL}/translation_editor_feedback`, JSON.stringify({ translation_editor_feedback: offlineTranslationFeedback }))
  }
  static getBookShelfList(){
    return getWithCredentials(`${NODE_API_URL}/lists/autocomplete`)
  }

  static addBookShelf(title, bookId){
    return postWithCredentials(`${NODE_API_URL}/lists?title=${title}&books[]=${bookId}&bookshelf=${true}`)
  }

  static addBookToBookshelf(bookshelfId, storyId){
    return postWithCredentials(`${NODE_API_URL}/lists/${bookshelfId}/add_story/${storyId}`)
  }

  static getBookshelfAutocomplete(str){
    return getWithCredentials(`${NODE_API_URL}/lists/autocomplete?query=${str}`)
  }

  static deleteList(listId) {
    return fetch(`${API_URL}/lists/${listId}`, {
      method: 'DELETE',
      credentials: 'include'
    })
  }

  static fetchSimilarBooks(slug) {
    return getWithCredentials(`${NODE_API_URL}/similar_stories/${slug}`);
  }
}

export class Home {
  static fetch(perPage = pagination.default) {
    return getWithCredentials(`${NODE_API_URL}/home?${queryString.stringify({perPage})}`)
  }

  static fetchBannerImages(profile) {
    return fetch(`${NODE_API_URL}/banners?${queryString.stringify({profile_type : profile})}`)
  }

  static fetchRecommendations(level,profile) {
    return getWithCredentials(`${NODE_API_URL}/home/recommendations?${queryString.stringify({reading_level : level,profile_type:profile})}`)
  }

  static fetchCards(stat) {
    return getWithCredentials(`${NODE_API_URL}/home/cards${stat ? '?static=true' : ''}`)
  }

  static updateSubmitStoryNotice(){
    return getWithCredentials(`${NODE_API_URL}/disable_notice`)
  }

  static fetchSpotlightCards(profile, lang){
    return fetch(`${NODE_API_URL}/spotlight_cards?${queryString.stringify({profile_type:profile})}`, {
      credentials: 'include',
      headers: {
        'locale': lang || localStorage.getItem('locale') || 'en'
      }
    });
  }

  static fetchTrendingLanguages(){
    return getWithCredentials(`${NODE_API_URL}/popular_languages`)
  }
  
  static fetchTrendingStories(profile= "all", level = 1){
    return getWithCredentials(`${NODE_API_URL}/trending_stories?reading_level=${level}&profile_type=${profile}`)
  }

  static fetchExplorations(profile){
    return getWithCredentials(`${NODE_API_URL}/exploration_cards?${queryString.stringify({profile_type:profile})}`)
  }

  static fetchTestimonials(profile){
    return getWithCredentials(`${NODE_API_URL}/testimonial_videos?${queryString.stringify({profile_type:profile})}`)
  }

  static fetchClassroomResources(){
    return getWithCredentials(`${NODE_API_URL}/classroom_resources`)
  } 

  static fetchEngagementContent() {
    return getWithCredentials(`${NODE_API_URL}/engagement_content`)
  } 

  static fetchTrendingThemes() {
    return getWithCredentials(`${NODE_API_URL}/trending_themes`);
  }
  
  static fetchSeoData() {
    return fetch(`${NODE_API_URL}/seo/meta_data?${queryString.stringify({url: window.location.href})}`, {
      method: 'GET',
      headers: {
        'Accept': 'application/json'
      }
    })
  }
}


export class Offline {
  static save(slug, imageSize, userId, type, enrollmentId, themeNumber, locale=null) {
    return fetch(`/offline-books/${slug}&imageSize=${imageSize}&userId=${userId}&type=${type}&enrollmentId=${enrollmentId}&themeNumber=${themeNumber}&locale=${locale}`, {
      method: 'POST'
    })
  }

  static unsave(slug, imageSize, userId, type, themeNumber) {
    return fetch(`/offline-books/${slug}&imageSize=${imageSize}&userId=${userId}&type=${type}&themeNumber=${themeNumber}`, {
      method: 'DELETE'
    })
  }

  static saveTranslation(slug, imageSize, userId, translation) {
    // to handle special char in language
    const encodedLanguage = encodeURIComponent(`${translation.targetLanguage}`);
    return fetch(`/offline-translate/${slug}&imageSize=${imageSize}&userId=${userId}&language=${encodedLanguage}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({translation})
    })
  }

  static updateTranslation(bookId, userId, translation) {
    const encodedLanguage = encodeURIComponent(`${translation.targetLanguage}`);
    return fetch(`/offline-translate/${bookId}&userId=${userId}&language=${encodedLanguage}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({translation})
    })
  }

  static unsaveTranslation(slug, imageSize, userId, language) {
    const encodedLanguage = encodeURIComponent(`${language}`);
    return fetch(`/offline-translate/${slug}&imageSize=${imageSize}&userId=${userId}&language=${encodedLanguage}`, {
      method: 'DELETE'
    })
  }

  static fetchTranslation(bookId, userId, language) {
    const encodedLanguage = encodeURIComponent(`${language}`);
    return fetch(`/offline-translate/${bookId}&userId=${userId}&language=${encodedLanguage}`);
  }

}

export class Image {
  static fetchAllFilters() {
    return getWithCredentials(`${NODE_API_URL}/illustrations/filters`);
  }

  static fetchAll(appliedFilters, page = 1, perPage = pagination.images) {
    const sanitizedFilters = sanitizeFilters(appliedFilters);

    const query = Object.assign({
      page,
      per_page: perPage
    }, sanitizedFilters);

    const qs = queryString.stringify(query, { arrayFormat: 'bracket' });
    return getWithCredentials(`${NODE_API_URL}/illustrations-search?${qs}`);
  }
}

export class Person {
  static fetchAll(appliedFilters, page = 1, perPage = pagination.images) {
    const sanitizedFilters = sanitizeFilters(appliedFilters);

    const query = Object.assign({
      page,
      per_page: perPage
    }, sanitizedFilters);

    const qs = queryString.stringify(query, { arrayFormat: 'bracket' });
    return getWithCredentials(`${NODE_API_URL}/people-search?${qs}`);
  }
}

export class Illustration {

  static fetch(slug, read) {
    return getWithCredentials(`${NODE_API_URL}/illustrations/${slug}${read ? '?read=true' : ''}`);
  }

  static fetchAllFilters() {
    return getWithCredentials(`${NODE_API_URL}/illustrations/filters`);
  }

  static fetchAllIllustrations(appliedFilters, page = 1, perPage = pagination.allIllustrations) {
    const query = {
      page,
      per_page: perPage
    };

    const optionalFilters = {
      category: 'categories',
      publisher: 'publishers',
      style: 'styles',
      illustrator: 'illustrators',
      query: 'query',
      tags: 'tags',
      sort: 'sort',
      status: 'status',
      bulk_download: 'bulk_download'
    };

    Object.keys(optionalFilters).forEach(f => {
      if (appliedFilters[f] && appliedFilters[f].length > 0) {
        query[optionalFilters[f]] = appliedFilters[f];
      }
    });

    const qs = queryString.stringify(query, { arrayFormat: 'bracket' });
    return getWithCredentials(`${NODE_API_URL}/illustrations-search?${qs}`);
  }

  static like(slug) {
    return postWithCredentials(`${NODE_API_URL}/illustrations/${slug}/like`);
  }

  static flag(slug, reasons) {
    return postWithCredentials(`${NODE_API_URL}/illustrations/${slug}/flag`, JSON.stringify(reasons));
  }

  static upload(formData) {
    return postMultiPartWithCredentials(`${API_URL}/illustration`, formData);
  }

  static tagsAutocomplete(searchValue) {
    return fetch(`${NODE_API_URL}/illustrations/autocomplete_tag_name?term=${searchValue}`)
  }

  static illustratorsAutocomplete(searchValue) {
    return fetch(`${NODE_API_URL}/illustrations/autocomplete_user_email?term=${searchValue}`)
  }

  static fetchNewFormData() {
    return fetch(`${NODE_API_URL}/illustration-fields`, {
      headers: {'locale': localStorage.getItem('locale') || 'en'}
    })
  }

  static fetchAdaptedAttributionText(parent_illustration_id) {
    return fetch(`${NODE_API_URL}/adapted_attribution_text?parent_illustration_id=${parent_illustration_id}`, {
      headers: {'locale': localStorage.getItem('locale') || 'en'}
    })
  }

  static fetchConfirmImageFormat(query) {
    return getWithCredentials(`${NODE_API_URL}/confirm_illustration_formats?${queryString.stringify(query)}`)
  }
}

export class ReadingProgram {
  static fetchHomepageContent(){
    return fetch(`${NODE_API_URL}/reading_program/home_page/static_content?locale=${window.location.search.split("=")[1] || 'en'}`)
  }
   static fetchFluences(){
     return fetch(`${NODE_API_URL}/fluences?locale=${window.location.search.split("=")[1] || 'en'}`)
   }
  static getLandingPageImages(){
    return fetch(`${NODE_API_URL}/reading_program_featurs?locale=${window.location.search.split("=")[1] || 'en'}`)
  }
   static getEnrolledFluencies(user_id) {
      return getWithCredentials(`${NODE_API_URL}/user_enrolled_fluences/${user_id}?locale=${window.location.search.split("=")[1] || 'en'}`)
   }

   static getFluenceProgress(fluence_id) {
     return fetch(`${NODE_API_URL}/reading_program/get_fluence_progress?fluence_id=${fluence_id}&locale=${window.location.search.split("=")[1] || 'en'}`)
   }
   static fluencyThemes(id) {
     return fetch(`${NODE_API_URL}/fluence/${id}?locale=${window.location.search.split("=")[1] || 'en'}`)
   }

   static themeDetails(fluencyLevel, themeNumber) {
    return fetch(`${NODE_API_URL}/fluence/${fluencyLevel}/theme/${themeNumber}?locale=${window.location.search.split("=")[1] || 'en'}`)
   }

   static enrollUser(userId, fluence_level, enrollment_type, children_in_class, is_from_cbse=false, is_from_goa=false, details){
     return fetch(`${NODE_API_URL}/user_enroll?id=${userId}&is_cbse=${is_from_cbse}&is_goa_rp=${is_from_goa}&locale=${window.location.search.split("=")[1] || 'en'}`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        "school_details": details,
        "enrollment_type": enrollment_type,
        "children_in_class": children_in_class,
        "fluence_level": fluence_level,
      }),
      headers: {
        'Content-Type': 'application/json'
      }
     })
   }

   static checkUserEnrolled(userId, fluencyLevel) {
      return fetch(`${NODE_API_URL}/is_user_enrolled?id=${userId}&fluence_level=${fluencyLevel}&locale=${window.location.search.split("=")[1] || 'en'}`)
   }

   static checkStoryStatus(fluence_level, theme_number, enrollmentId){
        return fetch(`${NODE_API_URL}/reading_program/get_story_status?level=${fluence_level}&theme_number=${theme_number}&enrollment_id=${enrollmentId}&locale=${window.location.search.split("=")[1] || 'en'}`)
   }

   static setStoryStatus(storyId, enrollmentId, theme_number, storyStatus){
     return postWithCredentials(`${NODE_API_URL}/reading_program/story_reading_status?story_id=${storyId}&enrollment_id=${enrollmentId}&theme_number=${theme_number}&reading_status=${storyStatus}&locale=${window.location.search.split("=")[1] || 'en'}`)
   }

   static getNextSuggestion(enrollmentId) {
      return fetch(`${NODE_API_URL}/reading_program/next_theme_suggestion?enrollment_id=${enrollmentId}&locale=${window.location.search.split("=")[1] || 'en'}`)
   }

   static getNextStorySuggestion(theme_id, enrollment_id) {
     return getWithCredentials(`${NODE_API_URL}/reading_program/next_story_suggestion?theme_id=${theme_id}&enrollment_id=${enrollment_id}&locale=${window.location.search.split("=")[1] || 'en'}`)
   }

   static getNextThemeSuggestion(enrollmentId) {
     return getWithCredentials(`${NODE_API_URL}/reading_program/next_theme_suggestion?enrollment_id=${enrollmentId}&locale=${window.location.search.split("=")[1] || 'en'}`)
   }

  static getThemeDetails(enrollmentId, themeNumber) {
    return getWithCredentials(`${NODE_API_URL}/enrollment/${enrollmentId}/theme/${themeNumber}?locale=${window.location.search.split("=")[1] || 'en'}`)
  }

  static getThemePageConstantData(fluencyLevel, themeNumber){
    return fetch(`${NODE_API_URL}/reading_program/home_page/static_content?locale=${window.location.search.split("=")[1] || 'en'}&fluence_level=${fluencyLevel}&theme_number=${themeNumber}`)
  }

  static getRpCertificateData(id) {
    return getWithCredentials(`${NODE_API_URL}/reading_program/rp_certificate?enrollment_id=${id}`)
  }

  static submitRpSurveyFormFeedback(postRpSurveyFormFeedbackPayload) {
    return postWithCredentials(`${NODE_API_URL}/reading_program_feedback`, JSON.stringify({ reading_program_feedback: postRpSurveyFormFeedbackPayload }))
  }

  static getGoaSchoolDetails(locale,udiseCode) {
    return fetch(`${NODE_API_URL}/auto_populate_school_details?locale=${locale}&udise_code=${udiseCode}`)
  }
}

export class MobileTranslate {
  static fetchPageContent(id) {
    return getWithCredentials(
      `${API_URL_V0}/editor/page/${id}/story_page_content`
    )
  }
  static updatePageContent(id, content){
    return postWithCredentials(`${NODE_API_URL}/update_page_content/${id}`, JSON.stringify(content))
  }

  static publish(story, id) {
    return fetch(`${API_URL}/mobile_translate/${id}/publish_mobile_translate`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({story, id})
  });
  }

  static saveToDraft(id) {
    return fetch(`${API_URL}/mobile_translate/draft_no_longer_dummy/${id}`, {
      method: 'PUT',
      credentials: 'include'
    })
  }

  static discardDraft(id) {
    return fetch(`${NODE_API_URL}/mobile_translate/delete_draft/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  }

  static updateStoryInfo(id, title, eng_title, synopsis = "") {
    return fetch(`${NODE_API_URL}/mobile_translate/${id}/update_story_info`, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      },
      credentials: 'include',
      body: formurlencoded({
        "title": title,
        "english_title": eng_title,
        "synopsis": synopsis
      })
    });
  }

  static fetchStoryInfo(id, isTemplate) {
    return getWithCredentials(`${NODE_API_URL}/mobile_translate/${id}/story_info?template=${isTemplate}`)
  }

  static fetchCheckTranslationsMobileTranslate(slug, translateToLanguage) {
    return getWithCredentials(
      `${NODE_API_URL}/mobile_translate/${slug}/check-translations?${queryString.stringify({translateToLanguage: translateToLanguage})}`
    )
  }

  static fetchAssetsForMobileTranslte(slug, ignoreCount=true) {
    return getWithCredentials(`${API_URL}/stories/${slug}/read?mobile_translate=${true}&ignore_count=${ignoreCount}&story_pages=${true}`);
  }

  static getStoryMobileTraslatability(slug) {
    return fetch(`${NODE_API_URL}/mobile_translate/${slug}/check_translation_eligibility`)
  }

  static fetchTransliteration(string, language) {
    return fetch(`https://quillpad.pbees.party/processWordJSON?inString=${string}&lang=${language}`)
  }

  static fetchVarnamTransliteration(string, language) {
    return fetch(`https://api.varnamproject.com/tl/${language}/${string}`)
  }

  static fetchAi4BharatTransliteration(string, language) {
    return fetch(`https://xlit-api1.ai4bharat.org/tl/${language}/${string}`)
  }

  static fetchMobileTranslateCoachmarkCount(userId, phaseName) {
    return getWithCredentials(`${NODE_API_URL}/mobile_translate/should_we_show_coachmark?user_id=${userId}&phase_name=${phaseName}`)
  }

  static updateMobileTranslateCoachmarkCount(userId, phaseName) {
    return fetch(`${NODE_API_URL}/mobile_translate/coachmark_shown?user_id=${userId}&phase_name=${phaseName}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include'
    })
  }

  static fetchAudioResponse(file, lang) {
    return fetch(`${API_URL}/mobile_translate/speech-to-text`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json'
      },
      credentials: 'include',
      body: JSON.stringify({data: file, lang: lang})
    })
  }
}

export class CsfProgram {

  static fetchCsfBooks( page , filters , routine , csfLocale) {

    const options = {
      page_number: page,
      locale : csfLocale,
      instructional_routine_identifier : routine
    }

    const optionalFilters = {
      level : 'level_number',
    };

    Object.keys(optionalFilters).forEach(f => {
      if (filters[f] && filters[f].length > 0) {
        options[optionalFilters[f]] = filters[f];
      }
    });

    return getWithCredentials(
      `${NODE_API_URL}/csf/lesson_listing_page/stories?${queryString.stringify(options, {arrayFormat: 'bracket'})}`
    )
  }
    static getStaticLandingText( csfLocale ){
      return fetch(`${NODE_API_URL}/csf/landing_page/static_content?locale=${csfLocale}`)
    }
    static getStaticLessonText(csfLocale){
      return fetch(`${NODE_API_URL}/csf/lesson_listing_page/static_content?locale=${csfLocale}`)
    }
    static getLevels(csfLocale){
      return fetch(`${NODE_API_URL}/csf/levels?locale=${csfLocale}`)
    }
    static getInstructionalRoutines(csfLocale){
      return fetch(`${NODE_API_URL}/csf/instructional_routines?locale=${csfLocale}`)
    }
    static getProgrammeOverviewCards(csfLocale){
      return fetch(`${NODE_API_URL}/csf/programme_overview/brief?locale=${csfLocale}`)
    }
    static getProgrammeOverview(csfLocale){
      return fetch(`${NODE_API_URL}/csf/programme_overview?locale=${csfLocale}`)
    }
    static getLevelFilters(csfLocale){
      return fetch(`${NODE_API_URL}/csf/lessons/filters?locale=${csfLocale}`)
    }
    static getRoutineBooks( instructionalRoutine , csfLocale){
      return fetch(`${NODE_API_URL}/csf/lesson_listing_page/stories_for_given_instructional_routine?locale=${csfLocale}&instructional_routine_identifier=${instructionalRoutine}`)
    }
    static getCsfResourceDetails(id, csfLocale){
      return fetch(`${NODE_API_URL}/csf/resource_details/${id}?locale=${csfLocale}`)
    }
    static postDownload( level, user, csfLocale) {
      return postWithCredentials(`${NODE_API_URL}/csf/send_resource_download_link?locale=${csfLocale}&level_number=${level}&user_id=${user}`)
    }

    static getSimilarBooks( id  , level , csfLocale ) {
      return fetch(`${NODE_API_URL}/csf/get_other_lessons?locale=${csfLocale}&curr_story_id=${id}&level_number=${level}`);
    }

}


export class EceProgram {
  static getEceStaticContent( locale, page ) {
    return fetch(`${NODE_API_URL}/ece/home_page/static_content?locale=${locale}&page_title=${page}`);
  }

  static getEceThemes( locale, page ) {
    return fetch(`${NODE_API_URL}/ece/themes?locale=${locale}&page_title=${page}`);
  }

  static getEcePosters( locale, page, posterArray ) {
    return fetch(`${NODE_API_URL}/ece/conversation_posters?locale=${locale}&page_title=${page}${posterArray.length > 0 ? `&posters=${posterArray}` : ''}`);
  }

  static getEceStories( theme_identifier, locale, filters)  {
    const options = {
      identifier: theme_identifier,
      locale : locale,
    }

    const optionalFilters = {
      level : 'reading_levels'
    };

    Object.keys(optionalFilters).forEach(f => {
      if (filters[f] && filters[f].length > 0) {
        options[optionalFilters[f]] = filters[f];
      }
    });

    return fetch(`${NODE_API_URL}/ece/stories?${queryString.stringify(options, {arrayFormat: 'bracket'})}`);
  }

  static getEceVideos( locale, page )  {
    return fetch(`${NODE_API_URL}/ece/videos?locale=${locale}&page_title=${page}`);
  }

  static getEceLevelFilters(locale){
    return fetch(`${NODE_API_URL}/ece/filters?locale=${locale}`)
  }

  static getEceResources(locale){
    return fetch(`${API_URL}/ece/resources?locale=${locale}`)
  }

  static postDownload( user, locale) {
    return fetch(`${NODE_API_URL}/ece/conversation_posters_download?locale=${locale}&user_id=${user}`)
  }
}

export class FlpHomeProgram {
  static getFlpHomeStaticContent( locale ) {
    return fetch(`${NODE_API_URL}/flp_at_home/landing_page_static_content?locale=${locale}`);
  }

  static getFlpHomeGrades( locale, grade, supplementary=false, userId ) {
    return fetch(`${NODE_API_URL}/flp_at_home/stories?locale=${locale}&grade=${grade}&type=flp_at_home${supplementary ? `&supplementary=${supplementary}` : ''}${userId? `&user_id=${userId}` : ''}`);
  }

  static getFlpHomeAboutContent( locale ) {
    return fetch(`${NODE_API_URL}/flp_at_home/understanding_programme_page_static_content?locale=${locale}`);
  }

  static flpHomeDownload( storyId, userId, locale) {
    return fetch(`${API_URL}/flp_at_home/story_download?locale=${locale}&story_id=${storyId}&user_id=${userId}&type=flp_at_home`)
  }

  static getResourceDetails(id, locale, userId){
    return fetch(`${NODE_API_URL}/flp_at_home/resource?id=${id}&locale=${locale}&type=flp_at_home&user_id=${userId}`)
  }

  static enrollFlpUser(userId, locale, grade, ps=null){
    let code = ps && ['hnp', 'np', 'tj'].includes(ps.toLowerCase()) ? ps.toLowerCase() : null
    return postWithCredentials(`${NODE_API_URL}/flp_user_enroll?user_id=${userId}&locale=${locale}&grade=${grade}${code ? `&ps=${code}`: ''}`)
  }

  static checkFlpUserEnrolled(userId, locale , grade){
    return fetch(`${NODE_API_URL}/is_flp_user_enrolled?user_id=${userId}&locale=${locale}&grade=${grade}`)
  }

  static checkStoryStatus(enrollmentId , locale , grade ){
    return fetch(`${NODE_API_URL}/flp_at_home/get_flp_story_status?flp_enrollment_id=${enrollmentId}&locale=${locale}&grade=${grade}`)
  }

  static setStoryStatus(storyId, enrollmentId, storyStatus , locale ){
    return postWithCredentials(`${NODE_API_URL}/flp_at_home/flp_story_reading_status?story_id=${storyId}&flp_enrollment_id=${enrollmentId}&flp_reading_status=${storyStatus}&locale=${locale}`)
  }

  static getNagCount(id, locale){
    return fetch(`${NODE_API_URL}/flp_at_home/read_count?locale=${locale}&user_id=${id}`)
  }

  static updateFlpUserData(enrollmentId, enrollmentData, locale) {
    return postWithCredentials(`${NODE_API_URL}/update_flp_enrollment_description?flp_enrollment_id=${enrollmentId}&enrollment_description=${enrollmentData}&locale=${locale}`)
  }

  static getFlpCertificateData(id, locale, name) {
    return getWithCredentials(`${NODE_API_URL}/flp_at_home/flp_certificate?locale=${locale}&flp_enrollment_id=${id}${name ? `&certificate_name=${name}`: ''}`)
  }

  static getFlpClassroomStaticContent( locale, userId ) {
    return fetch(`${NODE_API_URL}/flp-at-home/flp-classroom-static-content?locale=${locale}&type=flp_at_home&user_id=${userId}`);
  }

  static getAllStudents( locale, id, userId ) {
    return getWithCredentials(`${NODE_API_URL}/flp-at-home/students?locale=${locale}&flp_classroom_id=${id}&type=flp_at_home&user_id=${userId}`);
  }

  static getFlpValidateCode(code, locale, id) {
    return getWithCredentials(`${NODE_API_URL}/flp-at-home/validate-code?locale=${locale}&type=flp_at_home&code=${code}&user_id=${id}`)
  }
  
  static getAllClassrooms( locale, id ) {
    return getWithCredentials(`${NODE_API_URL}/flp-at-home/classrooms?locale=${locale}&user_id=${id}&type=flp_at_home`);
  }

  static flpAddClassroom(data, userId, locale) {
    return fetch(`${NODE_API_URL}/flp-at-home/classroom?locale=${locale}&type=flp_at_home&user_id=${userId}`, {
      method: 'POST',
      credentials: 'include',
      body: formurlencoded({
        'user_id': userId,
        'section': data.section.trim(),
        'school': data.school.trim(),
        'village': data.village.trim(),
        'city': data.city.trim(),
        'state': data.state,
        'grade': data.grade,
       
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static flpAddStudent(name, id, locale, userId) {
    return fetch(`${NODE_API_URL}/flp-at-home/student?locale=${locale}&type=flp_at_home&user_id=${userId}`, {
      method: 'POST',
      credentials: 'include',
      body: formurlencoded({
        'name': name.trim(),
        'flp_classroom_id': id
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static flpUpdateStudent(name, id, locale, userId) {
    return fetch(`${NODE_API_URL}/flp-at-home/student/${id}?locale=${locale}&type=flp_at_home&user_id=${userId}`, {
      method: 'POST',
      credentials: 'include',
      body: queryString.stringify({
        'name': name.trim(),
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static flpUpdateClassroom(data, id, locale, userId) {
    return fetch(`${NODE_API_URL}/flp-at-home/classroom/${id}?locale=${locale}&type=flp_at_home&user_id=${userId}`, {
      method: 'POST',
      credentials: 'include',
      body: queryString.stringify({
        'school': data.school.trim(),
        'section': data.section.trim(),
        'state': data.state,
        'village': data.village.trim(),
        'city': data.city.trim(),
      }),
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded'
      }
    })
  }

  static getAllFlpEnrollUser(userId, locale){
    return fetch(`${NODE_API_URL}/flp_user_enrollment_details?locale=${locale}&user_id=${userId}`);
  }

  static getAllFlpEnrollments(){
    return getWithCredentials(`${NODE_API_URL}/flp_at_home/enrollments`);
  }

  static getFlpAllStories (filters, locale, page=1, perPage = pagination.allBooks) {
    return fetch(`${NODE_API_URL}/flp_at_home/all_stories?${queryString.stringify({
      'instructional_routine_identifier[]' : filters.reading_strategy,
      'level_number[]' : filters.level,
      'grade[]': filters.grade,
      'locale' : locale,
      'page' : page,
      'per_page': perPage
    }) }`)
  }

  static validateWorksheet(id){
    return fetch(`${NODE_API_URL}/jotform/validate/worksheet/${id}`);
  }

  static addWorksheetQuestions(id){
    return fetch(`${NODE_API_URL}/jotform/add/worksheet/${id}`);
  }

  static viewWorksheetQuestions(id){
    return fetch(`${NODE_API_URL}/jotform/viewQuestions/worksheet/${id}`);
  }

  static viewWorksheetAnswers(id, enrolId, storyId){
    return fetch(`${NODE_API_URL}/jotform/viewAnswers/worksheet/${id}?enrollment_id=${enrolId}&story_id=${storyId}`);
  }

  static deleteSubmissions(id){
    return fetch(`${NODE_API_URL}/jotform/deleteSubmissions/worksheet/${id}`,{
      method: 'DELETE',
      credentials: 'include'
    });
  }
}

export class StemProgram {
  static getAllLessonPlans(subject, grade, locale) {
    return fetch(`${STRAPI_API}/lesson-plans?pagination[limit]=100&${queryString.stringify({
      'filters[subject][$containsi][]' : subject,
      'filters[grade][$endsWith][]' : grade
    })}&populate[activity][fields][0]=bookId&locale=${locale}`)
  }

  static getAllLessonPlansWithTag(tag, locale) {
    return fetch(`${STRAPI_API}/lesson-plans?pagination[limit]=100&${queryString.stringify({
      'filters[LPTags][tagName][$eq]' : tag,
    })}&populate[activity][fields][0]=bookId&&populate[LPTags][fields]&locale=${locale}`)
  }

  static getStemTagStaticData(locale) {
    return fetch(`${STRAPI_API}/stem-home-page?fields[0]=exploreLps&fields[1]=exploreBooks&fields[2]=exploreTags&locale=${locale}`)
  }

  static getStemStaticData(locale) {
    return fetch(`${STRAPI_API}/stem-home-page?populate=deep&locale=${locale}`)
  }

  static getStemTagSearchData(query, locale) {
    return fetch(`${STRAPI_API}/stem-tag-search?q=${query}&locale=${locale}`)
  }

  static getLessonPlanData(id) {
    return fetch(`${STRAPI_API}/lesson-plans/${id}?populate=deep`)
  }  

  static getLpStaticData(locale) {
    return fetch(`${STRAPI_API}/lesson-plan-static?locale=${locale}`)
  }

  static getLpFromSkillSet(skill, grade, subject) {
    return fetch(`${STRAPI_API}/stem-skills-stories?${queryString.stringify({
      'subject' : subject,
      'grade' : grade,
      'name' : skill
    })}`)
  } 

  static getLpFromBookId(id) {
    return fetch(`${STRAPI_API}/book-lp-data?bookId=${id}`)
  } 

  static getBooksForLp(bookArray, pledge=false) {
    return fetch(`${NODE_API_URL}/get_stem_story_data?story_ids=${bookArray.length> 0 ? bookArray.toString() : null}&pledge_to_read=${pledge}`)
  } 

  static getBookMarks(userId) {
    return fetch(`${STRAPI_API}/user/book-marks?user_id=${userId}`)
  }

  static getRecommended(userId, locale) {
    return fetch(`${STRAPI_API}/user/recommended-lp?user_id=${userId}&locale=${locale}`)
  }

  static addLPViews(userId, lpId) {
    return fetch(`${STRAPI_API}/user/lp-views?user_id=${userId}&lp_id=${lpId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  static addBookMark(userId, lpId) {
    return fetch(`${STRAPI_API}/user/book-marks?user_id=${userId}&lp_id=${lpId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  static deleteBookMark(userId, lpId) {
    return fetch(`${STRAPI_API}/user/book-marks?user_id=${userId}&lp_id=${lpId}`, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  static getStemLiteracyStaticData(locale) {
    return fetch(`${STRAPI_API}/stem-learn-more?populate=deep&locale=${locale}`)
  }

  static getStemMoocData(locale) {
    return fetch(`${STRAPI_API}/stem-mooc?populate=deep&locale=${locale}`)
  }

  static getStemMoocModuleData(id, locale) {
    return fetch(`${STRAPI_API}/stem-mooc?populate[module][populate]=%2A&populate[enrollModalData][populate]=*&populate[module][filters][url][$eq]=${id}&locale=${locale}`)
  }
  
  static getStemGroupingStaticData(locale) {
    return fetch(`${STRAPI_API}/stem-learn-more?fields[0]=shareText&fields[1]=backToStemTopics&fields[2]=booksRelatedToTopicName&locale=${locale}`)
  }

  static getStemExploreStaticData(subject, locale){
    return fetch (`${STRAPI_API}/groupings?populate[lesson_plans][populate][activity][fields][0]=bookId&populate[tileImage][fields][0]=url&[filters][subject][$eq][]=${subject}&locale=${locale}`)
  }

  static getStoriesStaticData(slugName, subject, locale){
    return fetch (`${STRAPI_API}/groupings?[filters][urlName][$eq][]=${slugName}&[filters][subject][$eq][]=${subject}&populate[lesson_plans][populate][activity][fields][0]=bookId&populate[bannerImage][fields][0]=url&populate[lesson_plans][populate][learnings][fields][1]=point&locale=${locale}`)
  }

  static getAllNewsData(limit){
    return fetch(`${STRAPI_API}/news-items?sort[0]=indvNewsPage.startDate:desc&populate=deep&pagination[pageSize]=${limit}`)
  }

  static getIndNewsData(slug){
    return fetch(`${STRAPI_API}/news-items?[filters][indvNewsPage][url][$eq][]=/${slug}&populate[indvNewsPage][fields][0]=title&populate[indvNewsPage][fields][1]=startDate&populate[newsSubCard][populate]=*&sort[0]=indvNewsPage.startDate:desc&populate=deep&publicationState=preview`)
  }

  static getAllCampaignsData() {
    return fetch(`${STRAPI_API}/campaigns?populate[campaignCard][populate][0]=cardImage&populate[campaignCard][populate][1]=assetImage`)
  }

  static getAllProgramCampaignsData() {
    return fetch(`${STRAPI_API}/programme-campaigns?populate[campaignCard][populate][0]=cardImage&populate[campaignCard][populate][1]=assetImage`)
  }

  static getIndCampaignsData(slug) {
    return fetch(`${STRAPI_API}/campaigns?[filters][campaignCard][url][$eq][]=/${slug}&populate=deep&publicationState=preview`)
  }

  static getIndProgramCampaignsData(slug, type=null) {
    if(type) {
      return fetch(`${STRAPI_API}/programme-campaigns?[filters][campaignType][$eq][]=${type}&populate=deep&publicationState=preview`)
    }
    return fetch(`${STRAPI_API}/programme-campaigns?[filters][campaignCard][url][$eq][]=/${slug}&populate=deep&publicationState=preview`)
  }

  static getAllPressReleaseData(limit) {
    return fetch(`${STRAPI_API}/press-releases?sort[0]=pressReleaseCard.startDate:desc&populate[pressReleaseCard][populate][0]=cardImage&populate[pressReleaseCard][populate][1]=assetImage&pagination[page]=1&pagination[pageSize]=${limit}`)
  }

  static getIndPressReleaseData(slug) {
    return fetch(`${STRAPI_API}/press-releases?[filters][pressReleaseCard][url][$eq][]=/${slug}&populate=deep&publicationState=preview`)
  }
  
  static enrolStemUser(data, locale) {
    return fetch(`${NODE_API_URL}/stem_user_enroll?locale=${locale}`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        "state": data.indianState,
        "district": data.indianDistrict,
        "student_count": data.numOfStudents,
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  static getStemEnrolment(locale) {
    return getWithCredentials(`${NODE_API_URL}/is_stem_user_enrolled?locale=${locale}`)
  }

  static setVideoView(module, video, locale, programType) {
    return fetch(`${NODE_API_URL}/${programType === "stem" ? 'set_stem_video_view_count' : 'set_flp_mooc_video_view_count'}?locale=${locale}`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        "video_id": video,
        "module_id": module
      }),
      headers: {
        'Content-Type': 'application/json'
      },
    })
  }

  static getFlpMoocData(locale) {
    return fetch(`${STRAPI_API}/flp-mooc?populate=deep&locale=${locale}`)
  }

  static getFlpMoocModuleData(id, locale) {
    return fetch(`${STRAPI_API}/flp-mooc?populate[flpModule][populate]=%2A&populate[enrollModalData][populate]=*&populate[flpModule][filters][url][$eq]=${id}&publicationState=preview&locale=${locale}`)
  }

  static enrolFlpMoocUser(data, locale) {
    return fetch(`${NODE_API_URL}/flp_mooc_user_enroll?locale=${locale}`, {
      method: 'POST',
      credentials: 'include',
      body: JSON.stringify({
        "state": data.indianState,
        "district": data.indianDistrict,
        "student_count": data.numOfStudents,
      }),
      headers: {
        'Content-Type': 'application/json'
      }
    })
  }

  static getFlpMoocEnrolment(locale) {
    return getWithCredentials(`${NODE_API_URL}/is_flp_mooc_user_enrolled?locale=${locale}`)
  }

  static enrollPledgeCampaign(details, org) {
    return postWithCredentials(`${NODE_API_URL}/campaign/pledge_to_read`, JSON.stringify({
      'state': details.state,
      'country': details.country,
      'district': details.district,
      'no_of_children': details.noOfChildren,
      'org': org
    }));
  }

  static getPledgeCampaignEnroll(org) {
    console.log(org)
    let orgString = `?org=${org}`
    return getWithCredentials(`${NODE_API_URL}/campaign/has_user_already_pledged${org ? orgString : ''}`)
  }

  static getStaticPledgeData(locale) {
    return fetch(`${STRAPI_API}/pledge-to-read?populate=deep&locale=${locale}&publicationState=preview`)
  }

  static getStaticSubCampaignData(locale, org) {
    return fetch(`${STRAPI_API}/gcr-sub-campaigns?[filters][partnerName][$eq][]=${org}&populate=deep&locale=${locale}&publicationState=preview`)
  }

  static getStaticPledgePartnerData(locale) {
    return fetch(`${STRAPI_API}/pledge-to-read-partner?populate=deep&locale=${locale}&publicationState=preview`)
  }

  static getHeroActivity(locale, org) {
    if(org) 
      return fetch(`${STRAPI_API}/gcr-sub-campaigns?fields[0]=activityTitle&populate[heroBooks][fields]&locale=${locale}&publicationState=preview&filters[partnerName]=${org}`)
    else
      return fetch(`${STRAPI_API}/${isOdosWeek ? "pledge-to-read" : "get-children-reading"}?fields[0]=activityTitle&populate[heroBooks][fields]&locale=${locale}&publicationState=preview`)
  }

  static getFlagshipVideos(locale) {
    return fetch(`${STRAPI_API}/${isOdosWeek ? "pledge-to-read" : "get-children-reading"}?fields[0]=videoHeading&fields[1]=aboutUsTitle&fields[2]=aboutUsDesc&fields[3]=aboutCampaignBtn&fields[4]=joinCampaignBtn&fields[5]=share&populate[videoBanner][fields][0]=url&populate[videoBannerMobile][fields][0]=url&populate[video][fields]&locale=${locale}&publicationState=preview`)
  }

  static markPTRNotifAsRead() {
    return postWithCredentials(`${NODE_API_URL}/campaign/user_ptr_weekly_notification_status?read_notification=true`);
  }
  
  static getStemMoocCertificateData(enrollId, name, moduleId, moduleName, schoolName = null, locale, moduleIndex) {
    return getWithCredentials(`${NODE_API_URL}/stem_mooc/stem_certificate?locale=${locale}&stem_enrollment_id=${enrollId}&certificate_name=${name}&module=${moduleId}&module_name=${moduleName}&school_name=${schoolName}&module_index=${moduleIndex}`)
  }

  static getStemMoocAllModules(locale) {
    return fetch(`${STRAPI_API}/stem-mooc?fields[0]=id&populate[module][fields][0]=name&populate[module][fields][1]=url&populate[module][populate][image][fields][0]=url&locale=${locale}`)
  }

  static getFlpMoocCertificateData(enrollId, name, moduleId, moduleName, schoolName = null, locale, moduleIndex) {
    return getWithCredentials(`${NODE_API_URL}/flp_mooc/flp_mooc_certificate?locale=${locale}&flp_mooc_enrollment_id=${enrollId}&certificate_name=${name}&module=${moduleId}&module_name=${moduleName}&school_name=${schoolName}&module_index=${moduleIndex}`)
  }

  static getFlpMoocAllModules(locale) {
    return fetch(`${STRAPI_API}/flp-mooc?fields[0]=id&populate[flpModule][fields][0]=name&populate[flpModule][fields][1]=url&populate[flpModule][populate][image][fields][0]=url&locale=${locale}`)
  }

  static partnerPtrForm(certDetails, locale, certificate) {
    return postWithCredentials(`${NODE_API_URL}/campaign/pledge_to_read_partner_detail`, JSON.stringify({
      'organization': certDetails.org,
      'story_books': certDetails.storyBooks,
      'reading_experience': certDetails.feedback,
      'certificate_name': certDetails.certName,
      'pledge_to_read_data_id': certDetails.id,
      'locale': locale,
      'certificate': certificate || null
    }));
  }

  static getStemSearchResults(query, grade=[], subject=[], locale=[]) {
    let grades = (grade && grade.length > 0) ? grade.map(el=> `Grade ${el}`) : [];

    return fetch(`${STRAPI_API}/stem-search?q=${query}&${queryString.stringify({
      'grade[]': grades,
      'subject[]':subject,
      'locale[]':locale,
    })}`)
  }

  static getGcrReadingCertificate(name, badge, locale, org, data) {
    return postWithCredentials(`${NODE_API_URL}/campaign/pledge_to_read_user_detail`, JSON.stringify({
      'badge': badge,
      'locale': locale,
      'org': org || "StoryWeaver",
      'certificate_name': name,
      'certificate': data || null
    }));
  }

  static getStaticSubGcrData() {
    return fetch(`${STRAPI_API}/gcr-sub-campaigns?fields[0]=menuPartnerName&fields[1]=partnerName&populate[0]=menuPartnerLogo&populate[1]=languageSupport&populate=deep`)
  }

  static getImpactStaticData(slug) {
    return fetch(`${STRAPI_API}/impact-static-pages?[filters][impactCard][url][$eq][]=/${slug}&populate=deep&publicationState=preview`)
  }

  static getAwardsStaticData(){
    return fetch(`${STRAPI_API}/awards-certificates?populate=deep`)
  }

  static getAllImpactsData() {
    return fetch(`${STRAPI_API}/impact-static-pages?populate[impactCard][populate][0]=cardImage&populate[impactCard][populate][1]=assetImage`)
  }

  static getOdosCampaignEnroll() {
    return getWithCredentials(`${NODE_API_URL}/campaign/odos/has_user_pledged`)
  }

  static enrollOdosCampaign(details) {
    let sanitizeDetails = {
      'state': details.state,
      'country': details.country,
      'district': details.district || null,
      'noOfChildren': details.noOfChildren
    }

    return postWithCredentials(`${NODE_API_URL}/campaign/odos/enroll_to_odos`, JSON.stringify({details: sanitizeDetails}));
  }

  static odosUserDetails(userDetails) {
    return postMultiPartWithCredentials(`${NODE_API_URL}/campaign/odos/odos_user_detail`, userDetails);
  }

  static getOdosCertificate(certDetails, locale) {
    return postWithCredentials(`${NODE_API_URL}/campaign/odos/download_certificate`, JSON.stringify({
      'locale': locale,
      'certificate': certDetails || null
    }));
  }

  static uploadFileToCloud(formData) {
    return postMultiPartWithCredentials(`${NODE_API_URL}/upload-file`, formData);
  }

  static removeFileFromCloud(file) {
    return fetch(`${NODE_API_URL}/upload-file?file=${file}`, {
      method: 'DELETE',
      credentials: 'include',
    });
  }

  static imageAnalyze(formData) {
    return postMultiPartWithCredentials(`${NODE_API_URL}/image`, formData);
  }

  static getOldGcrPledgeData(locale) {
    return fetch(`${STRAPI_API}/get-children-reading?populate=deep&locale=${locale}&publicationState=preview`)
  }

  static getOdosCertData(locale) {
    return fetch(`${STRAPI_API}/sp-odos-certificate?populate=deep&locale=${locale}&publicationState=preview`)
  }

  static getCareerHomePageStaticData() {
    return fetch(`${STRAPI_API}/careers?populate=deep&fields[0]=jobTitle&fields[1]=jobLocation&fields[2]=url&fields[3]=published_at`)
  }

  static getCareerDetailPageStaticData(slug) {
    return fetch(`${STRAPI_API}/careers?[filters][url][$eq][]=${slug}&publicationState=preview`)
  }

  static getResourcesLandingPageData(slug, locale) {
    return fetch(`${STRAPI_API}/resource-landing-pages?filters[url][$eq]=${slug}&locale=${locale}&populate=deep&publicationState=preview`)
  }

  static getResourceStories(category, language, isAudio) {
    return fetch(`${NODE_API_URL}/resource_stories?category=${category}&language=${language}&is_audio=${isAudio}`)
  }

  static getResourceStoriesLanguages(category, isAudio) {
    return fetch(`${NODE_API_URL}/resource_stories_languages?category=${category}&is_audio=${isAudio}`)
  }

  static getAllCampaignRegistrations() {
    return fetch(`${STRAPI_API}/programme-campaigns?populate[section][populate][enrollSection][populate][bookshelfData][populate][0]=listId&populate[section][fields][1]=enrollSection&populate[section][populate][enrollSection][fields][1]=title&fields[0]=campaignType&populate[campaignCard][fields][2]=url&populate[certificateData][populate][leftSignature][populate][sign]=*&populate[certificateData][populate][rightSignature][populate][sign]=*&populate[certificateData][populate][middleSignature][populate][sign]=*&populate[certificateData][populate][logos][populate]=*&populate[certificateData][populate][backgroundImg]=*`)
  }

  static getEventCertificateStaticData(slugName, locale){
    return fetch (`${STRAPI_API}/event-type-certificates?[filters][eventType][$eq][]=${slugName}&populate=deep&publicationState=preview&locale=${locale}`)
  }

  static getEventCertificateDetails(eventType) {
    return getWithCredentials(`${NODE_API_URL}/event-type-certificate/details?event_type=${eventType}`)
  }

  static eventCertificateEnroll(eventType, userDetails, language) {
    return postWithCredentials(`${NODE_API_URL}/event-type-certificate/registration?event_type=${eventType}&language=${language}`, JSON.stringify({
      certificate_name: userDetails.certificateName,
      country: userDetails.country,
      state: userDetails.state,
      campaign_name: userDetails.campaignName,
      email: userDetails.email,
      phone_number: userDetails.phoneNumber,
      district: userDetails.district,
      text_box: userDetails.textBox,
      completed_training_date: userDetails.completedTrainingDate,
      check_box_consent: userDetails.checkBoxConsent,
      media_upload: userDetails.mediaUpload,
    }));
  }

  static getEventCertificate(certDetails, locale, id, eventType) {
    return postWithCredentials(`${NODE_API_URL}/event-type-certificate/download_certificate`, JSON.stringify({
      'locale': locale,
      'certificate': certDetails || null,
      'registration_id': id,
      'event_type': eventType
    }));
  }

  static getEventLangSupportData(slugName, locale){
    return fetch (`${STRAPI_API}/event-type-certificates?[filters][eventType][$eq][]=${slugName}&populate[languageSupport]=*&populate=deep&publicationState=preview&locale=all`)
  }
}



// WEBPACK FOOTER //
// ./src/api.js